import React from 'react';
import { PlatformType } from '@/types/index.js';

interface PlatformBadgeProps {
  platform?: PlatformType | string | null;
  className?: string;
  size?: 'xs' | 'sm' | 'md';
  showIcon?: boolean;
}

export function PlatformBadge({
  platform,
  className = '',
  size = 'sm',
  showIcon = true,
}: PlatformBadgeProps) {
  const norm = (platform || 'direct').toLowerCase() as PlatformType;

  // Branded styling configuration
  let label = 'Direct Client';
  let badgeClasses = 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800/50';
  let iconElement: React.ReactNode = (
    <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <polyline points="16 11 18 13 22 9" />
    </svg>
  );

  switch (norm) {
    case 'fiverr':
      label = 'Fiverr';
      badgeClasses = 'bg-[#1dbf73]/10 text-[#0f8c51] dark:text-[#22c55e] border-[#1dbf73]/30 dark:bg-[#1dbf73]/15 dark:border-[#1dbf73]/40';
      iconElement = (
        <span className="font-black text-[10px] tracking-tighter leading-none bg-[#1dbf73] text-white px-1 py-0.5 rounded-sm">
          fi
        </span>
      );
      break;

    case 'upwork':
      label = 'Upwork';
      badgeClasses = 'bg-[#14a800]/10 text-[#0f8200] dark:text-[#4ade80] border-[#14a800]/30 dark:bg-[#14a800]/15 dark:border-[#14a800]/40';
      iconElement = (
        <span className="font-black text-[10px] tracking-tighter leading-none bg-[#14a800] text-white px-1 py-0.5 rounded-sm">
          up
        </span>
      );
      break;

    case 'internal':
      label = 'Internal';
      badgeClasses = 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800/50';
      iconElement = (
        <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      );
      break;

    case 'other':
      label = 'Other';
      badgeClasses = 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800/60 dark:text-slate-300 dark:border-slate-700';
      iconElement = (
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 flex-shrink-0" />
      );
      break;

    case 'direct':
    default:
      label = 'Direct Client';
      badgeClasses = 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800/50';
      break;
  }

  const sizeClasses = {
    xs: 'text-[10px] px-1.5 py-0.5 gap-1',
    sm: 'text-xs px-2 py-0.5 gap-1.5',
    md: 'text-xs px-2.5 py-1 gap-1.5 font-medium',
  }[size];

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium border shadow-xs transition-colors whitespace-nowrap ${badgeClasses} ${sizeClasses} ${className}`}
      title={`Platform: ${label}`}
    >
      {showIcon && iconElement}
      <span>{label}</span>
    </span>
  );
}
